import type { ThemeMode } from '@/components/theme-provider';

/**
 * Capacitor StatusBar Style naming is counter-intuitive:
 * - Style.Dark  → light/white icons (for dark backgrounds)
 * - Style.Light → dark icons (for light backgrounds)
 */
export type CapacitorStatusBarStyle = 'DARK' | 'LIGHT';

export const NATIVE_STATUS_BAR = {
  light: { bg: '#F1F1F1', capacitorStyle: 'LIGHT' as const },
  dark: { bg: '#000000', capacitorStyle: 'DARK' as const },
} satisfies Record<ThemeMode, { bg: string; capacitorStyle: CapacitorStatusBarStyle }>;

export function nativeStatusBarForTheme(theme: ThemeMode) {
  return NATIVE_STATUS_BAR[theme];
}

export function themeFromDocumentClassList(classList: DOMTokenList | { contains: (c: string) => boolean }): ThemeMode {
  return classList.contains('dark') ? 'dark' : 'light';
}

/** Match Capacitor status bar fill to CSS `--background` when available. */
export function readDocumentBackgroundColor(): string {
  if (typeof document === 'undefined') return NATIVE_STATUS_BAR.light.bg;
  const cssBg = getComputedStyle(document.documentElement).getPropertyValue('--background').trim();
  if (/^#[0-9a-f]{3,8}$/i.test(cssBg)) return cssBg;
  return nativeStatusBarForTheme(themeFromDocumentClassList(document.documentElement.classList)).bg;
}

import { applyNativeSystemBars } from '@/lib/native-system-bars';

export async function applyCapacitorStatusBarTheme(theme: ThemeMode): Promise<void> {
  const { StatusBar, Style } = await import('@capacitor/status-bar');
  const { bg: themeBg, capacitorStyle } = nativeStatusBarForTheme(theme);
  const cssBg = readDocumentBackgroundColor();
  const bg = cssBg.toLowerCase() === themeBg.toLowerCase() ? cssBg : themeBg;
  const lightStatusBarAppearance = capacitorStyle === 'LIGHT';

  await StatusBar.setOverlaysWebView({ overlay: false });
  await StatusBar.show();
  await StatusBar.setStyle({
    style: lightStatusBarAppearance ? Style.Light : Style.Dark,
  });
  await StatusBar.setBackgroundColor({ color: bg });
  await applyNativeSystemBars(bg, lightStatusBarAppearance);
}

export async function syncCapacitorStatusBarWithDocument(): Promise<void> {
  if (typeof document === 'undefined') return;
  await applyCapacitorStatusBarTheme(themeFromDocumentClassList(document.documentElement.classList));
}

/** SplashScreen can swallow the first StatusBar.setStyle — re-apply a few times. */
export function scheduleCapacitorStatusBarSync(): void {
  const run = () => {
    void syncCapacitorStatusBarWithDocument().catch(() => {});
  };
  run();
  for (const delayMs of [300, 900, 2200, 4000]) {
    window.setTimeout(run, delayMs);
  }
}
